import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appApi, coachApi, profileApi, type CollectItem } from "@/api";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { auth } from "@/lib/auth";
import { locationStore } from "@/lib/location";
import { buildLoginRedirect } from "@/lib/navigation";

interface FollowedTherapistView {
  id: number;
  coachId: number;
  relationId: number;
  name: string;
  level: string;
  levelStyle: { color: string; bg: string };
  status: string;
  rating: string;
  orders: number;
  years: string;
  specialties: string[];
  avatar: string;
  earliestTime: string;
  distance: string;
}

function getLevelStyle(level: string) {
  if (level === "高级") {
    return { color: "#C8860A", bg: "#FFF8E7" };
  }
  if (level === "新人") {
    return { color: "#FF7B22", bg: "#FFF3EC" };
  }
  return { color: "#8B6FE8", bg: "#F2EEFF" };
}

function getStatusText(serverStatus?: number, workStatus?: number) {
  if (typeof serverStatus === "number" && serverStatus !== 1) {
    return "暂停服务";
  }
  return Number(workStatus || 0) === 1 ? "可服务" : "服务中";
}

function mapCollectedCoach(item: CollectItem, detail?: Awaited<ReturnType<typeof coachApi.getCoachDetail>>): FollowedTherapistView {
  const level = String(detail?.level || "平台技师");
  const coachId = Number(item.relation_id || item.id || detail?.id || 0);

  return {
    id: coachId,
    coachId,
    relationId: coachId,
    name: String(detail?.name || item.name || "技师"),
    level,
    levelStyle: getLevelStyle(level),
    status: getStatusText(item.server_status, item.work_status ?? detail?.work_status),
    rating: Number(detail?.display_rating || detail?.score_comment || 0).toFixed(1),
    orders: Number(detail?.display_order_num || detail?.order_num || item.order_num || 0),
    years: detail?.age ? `${detail.age}岁` : "平台认证",
    specialties: Array.isArray(detail?.specialties) && detail.specialties.length ? detail.specialties : ["上门服务"],
    avatar:
      resolveCustomerAssetUrl(String(detail?.work_photo || item.work_photo || "")) ||
      "https://readdy.ai/api/search-image?query=professional%20massage%20therapist%20portrait&width=160&height=160&seq=followed-avatar&orientation=squarish",
    earliestTime: String(detail?.first_appoint || item.first_appoint || "尽快上门"),
    distance: String(detail?.distance_desc || item.distance_desc || ""),
  };
}

export default function FollowedTherapistsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [followedTherapists, setFollowedTherapists] = useState<FollowedTherapistView[]>([]);
  const [unfollowConfirm, setUnfollowConfirm] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadFollowedTherapists = async () => {
      if (!auth.getToken()) {
        navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
        return;
      }

      setLoading(true);
      setError("");

      try {
        const currentLocation = locationStore.getFresh();
        const collectResult = await profileApi.getCollectList({
          type: 1,
          page_no: 1,
          page_size: 50,
        });
        const collectItems = Array.isArray(collectResult.lists) ? collectResult.lists : [];

        const detailResults = await Promise.allSettled(
          collectItems.map((item) =>
            coachApi.getCoachDetail(item.relation_id || item.id, {
              longitude: currentLocation?.longitude,
              latitude: currentLocation?.latitude,
            }),
          ),
        );

        if (cancelled) {
          return;
        }

        const nextTherapists = collectItems
          .map((item, index) => {
            const detailResult = detailResults[index];
            const detail = detailResult.status === "fulfilled" ? detailResult.value : undefined;
            return mapCollectedCoach(item, detail);
          })
          .filter((item) => item.coachId > 0);

        setFollowedTherapists(nextTherapists);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : "关注技师加载失败");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadFollowedTherapists();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  const followedIds = useMemo(() => followedTherapists.map((item) => item.coachId), [followedTherapists]);

  const goToHomeTherapistList = () => {
    navigate("/");
  };

  const handleUnfollow = async (relationId: number) => {
    try {
      await appApi.toggleCollect({ id: relationId, type: 1 });
      setFollowedTherapists((prev) => prev.filter((item) => item.relationId !== relationId));
      setUnfollowConfirm(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "取消关注失败");
      setUnfollowConfirm(null);
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ background: "#F7F5F2", maxWidth: 480, margin: "0 auto" }}
    >
      <div
        className="sticky top-0 z-30 flex items-center gap-3 px-4 pt-12 pb-4"
        style={{ background: "#fff", borderBottom: "1px solid #F0EDE8" }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer flex-shrink-0"
          style={{ background: "#F5F2EE" }}
        >
          <i className="ri-arrow-left-s-line text-lg" style={{ color: "#555" }} />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold" style={{ color: "#1A1A1A" }}>关注的技师</h1>
          <p className="text-xs mt-0.5" style={{ color: "#AAAAAA" }}>共 {followedIds.length} 位</p>
        </div>
        <button
          onClick={goToHomeTherapistList}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap"
          style={{ background: "#FFF2E8", border: "1px solid #FFD4A0" }}
        >
          <i className="ri-compass-3-line text-xs" style={{ color: "#FF6018" }} />
          <span className="text-xs font-medium" style={{ color: "#FF6018" }}>发现更多</span>
        </button>
      </div>

      <div className="flex-1 px-4 py-4">
        {error ? (
          <div
            className="mb-4 rounded-2xl px-4 py-3 text-sm"
            style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
          >
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="py-10 text-center text-sm" style={{ color: "#AAA" }}>加载中...</div>
        ) : followedTherapists.length === 0 ? (
          <div className="flex flex-col items-center pt-20">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
              style={{ background: "#FFF2E8" }}
            >
              <i className="ri-user-heart-line text-4xl" style={{ color: "#FFBA8A" }} />
            </div>
            <p className="text-base font-bold mb-1.5" style={{ color: "#1A1A1A" }}>还没有关注的技师</p>
            <p className="text-sm text-center leading-relaxed mb-6" style={{ color: "#AAAAAA" }}>
              还没有关注任何技师
              <br />
              从首页预约后可关注喜欢的技师
            </p>
            <button
              onClick={goToHomeTherapistList}
              className="px-6 py-3 rounded-full text-white text-sm font-bold cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #FF7A30, #FF5000)" }}
            >
              去看看
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {followedTherapists.map((item) => (
              <FollowedTherapistCard
                key={item.id}
                therapist={item}
                onUnfollowRequest={() => setUnfollowConfirm(item.relationId)}
                onBook={() => navigate(`/therapist-detail?id=${item.coachId}&showService=true`)}
                onDetail={() => navigate(`/therapist-detail?id=${item.coachId}`)}
              />
            ))}

            <div className="flex items-center justify-center py-4 gap-1.5">
              <div className="w-8 h-px" style={{ background: "#E8E0D5" }} />
              <p className="text-xs" style={{ color: "#CCBDAD" }}>已显示全部关注技师</p>
              <div className="w-8 h-px" style={{ background: "#E8E0D5" }} />
            </div>
          </div>
        )}
      </div>

      {unfollowConfirm !== null ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-8"
          style={{ background: "rgba(0,0,0,0.45)", maxWidth: 480, margin: "0 auto", left: "50%", transform: "translateX(-50%)" }}
          onClick={() => setUnfollowConfirm(null)}
        >
          <div
            className="w-full rounded-3xl p-6"
            style={{ background: "#fff" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center mb-5">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
                style={{ background: "#FFF2E8" }}
              >
                <i className="ri-user-unfollow-line text-2xl" style={{ color: "#FF6018" }} />
              </div>
              <p className="text-base font-bold mb-1" style={{ color: "#1A1A1A" }}>取消关注</p>
              <p className="text-sm leading-relaxed" style={{ color: "#999" }}>
                取消后将不再收到该技师的
                <br />
                空档通知，确定取消吗？
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setUnfollowConfirm(null)}
                className="py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
                style={{ background: "#F5F2EE", color: "#666" }}
              >
                再想想
              </button>
              <button
                onClick={() => void handleUnfollow(unfollowConfirm)}
                className="py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap text-white"
                style={{ background: "linear-gradient(135deg, #FF7A30, #FF5000)" }}
              >
                确认取消
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FollowedTherapistCard({
  therapist: item,
  onUnfollowRequest,
  onBook,
  onDetail,
}: {
  therapist: FollowedTherapistView;
  onUnfollowRequest: () => void;
  onBook: () => void;
  onDetail: () => void;
}) {
  const statusColor =
    item.status === "可服务" ? "#17B978" : item.status === "服务中" ? "#FF7B22" : "#BBBBBB";

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: "#fff", border: "1px solid #EEEAE4" }}
    >
      <button
        onClick={onDetail}
        className="w-full flex items-start gap-3.5 p-4 cursor-pointer text-left"
      >
        <div className="relative flex-shrink-0">
          <div
            className="w-[60px] h-[60px] rounded-2xl overflow-hidden"
            style={{ border: "2px solid #F0EAE0" }}
          >
            <img src={item.avatar} alt={item.name} className="w-full h-full object-cover object-top" />
          </div>
          <div
            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
            style={{ background: "#fff" }}
          >
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ background: statusColor }}
            />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{item.name}</span>
            <span
              className="text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
              style={{ background: item.levelStyle.bg, color: item.levelStyle.color }}
            >
              {item.level}
            </span>
            <span className="text-[10px] flex-shrink-0" style={{ color: statusColor }}>
              {item.status}
            </span>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-0.5">
              <i className="ri-star-fill text-[11px]" style={{ color: "#FF7B22" }} />
              <span className="text-xs font-bold" style={{ color: "#FF7B22" }}>{item.rating}</span>
            </div>
            <span className="text-[11px]" style={{ color: "#D0C4B8" }}>路</span>
            <span className="text-[11px]" style={{ color: "#AAAAAA" }}>{item.orders} 单</span>
            <span className="text-[11px]" style={{ color: "#D0C4B8" }}>路</span>
            <span className="text-[11px]" style={{ color: "#AAAAAA" }}>{item.years}</span>
          </div>

          <div className="flex flex-wrap gap-1">
            {item.specialties.slice(0, 3).map((specialty) => (
              <span
                key={specialty}
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: "#FFF4EC", color: "#FF6018" }}
              >
                {specialty}
              </span>
            ))}
          </div>
        </div>
      </button>

      {item.status === "可服务" ? (
        <div
          className="mx-4 mb-3 flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ background: "#F7F5F2" }}
        >
          <i className="ri-time-line text-xs flex-shrink-0" style={{ color: "#FF6018" }} />
          <span className="text-xs" style={{ color: "#888" }}>
            今日最早可约
            <span className="font-semibold ml-1" style={{ color: "#FF6018" }}>{item.earliestTime}</span>
          </span>
          <span className="text-[11px] ml-auto" style={{ color: "#BBBBBB" }}>{item.distance}</span>
        </div>
      ) : null}

      {item.status === "服务中" ? (
        <div
          className="mx-4 mb-3 flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ background: "#FFF8F0" }}
        >
          <i className="ri-loader-4-line text-xs flex-shrink-0" style={{ color: "#FF7B22" }} />
          <span className="text-xs" style={{ color: "#FF7B22" }}>服务中，预计今日 {item.earliestTime} 后空闲</span>
        </div>
      ) : null}

      <div className="flex items-center gap-2 px-4 pb-4">
        <button
          onClick={onUnfollowRequest}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap"
          style={{ background: "#F5F2EE", color: "#999", border: "1px solid #EAE6E0" }}
        >
          <i className="ri-heart-fill text-sm" style={{ color: "#FF6018" }} />
          已关注
        </button>

        <button
          onClick={onBook}
          className="flex-1 py-2.5 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap text-white"
          style={{
            background: item.status === "可服务"
              ? "linear-gradient(135deg, #FF7A30, #FF5000)"
              : "#E8E0D5",
            color: item.status === "可服务" ? "#fff" : "#AAAAAA",
          }}
          disabled={item.status !== "可服务"}
        >
          {item.status === "可服务" ? "立即预约" : "暂不可约"}
        </button>
      </div>
    </div>
  );
}
